/**
 * building_interiors.ts — PURE foundation for walkable building interiors
 * (2026-08-13). The ship-deck doctrine brought to the ground: interiors are
 * SAME-SCENE procedural shells built inside the building's real footprint at
 * its real position — windows are true openings, so the view through the
 * glass IS the area you were standing in before you walked in (no cells, no
 * portals, no render targets).
 *
 * Eligibility: living quarters, civic halls, and hall-scale structures only —
 * habitat towers, civic buildings, guild halls, player houses. Mining rigs,
 * extractors, kiosks, props, and anything smaller than a real room stay
 * solid (no interior).
 *
 * Multi-level: floors every FLOOR_H metres up the building's true height
 * (tall towers get many), joined by switchback-free straight stair runs with
 * a slab opening above each run. The KCC walks it all on plain box colliders
 * (structure-floor grounding is already client physics law).
 */
import { admits, deedCapability, settlementCapability, type InteriorCapability } from './interior_capability.js';

export const FLOOR_H = 3.4;
export const MAX_FLOORS = 8;
const WALL_IN = 0.45;   // shell inset from the footprint
const SILL_H = 1.0;     // window sill height
const HEADER_H = 2.7;   // window header height
const DOOR_W = 1.9;
const DOOR_H = 2.6;

// ─── THE LIFT SHAFT (the interiors programme, slice L, 2026-10-01) ───────────────────────────────────────────────────
// A shell of ≥ 3 floors carries a lift shaft where the PLANNER reserves its candidate (`interior_planner.ts
// liftCandidateFor` — the same rule, mirrored here because this file imports nothing: it is mirrored to the server).
// `verify:interior-lift` pins `interiorSpec(...).lift.rect` to `planInterior(...).descriptor.liftCandidate.rect` over
// every lift-eligible identity and a size sweep: change one, change both.
//
// EVERYTHING THE SHAFT ADDS LIES INSIDE THE CANDIDATE'S 1.6 × 1.6 m RECT. The planner reads this shell's collide boxes
// back (solids, the route raster, the floored cells) and already holds that rect solid — so the shaft moves no route,
// no anchor and no digest.
//
// THE SHAFT IS SOLID EXCEPT WHERE THE CAB STANDS OPEN: each storey's cell is filled by one collide-only PLUG, so the
// shell is airtight by itself (laws 6–8) whatever the lift module does; the module removes the docked floor's plug —
// and the plug BELOW the docked cell is the floor a rider stands on (3.4 m of solid: law 7). See lib/interior_lift.ts.

/** The shaft's side (m) and the apron kept clear before its door — MIRRORS of the planner's `LIFT_M` / `LIFT_APRON_M`. */
export const LIFT_SHAFT_M = 1.6, LIFT_APRON_M = 1.1;
/** The shaft's two side walls (m thick, inside the rect), the landing door's clear height, the front wall's depth. */
export const LIFT_SIDE_WALL_M = 0.2, LIFT_DOOR_H = 2.2, LIFT_FRONT_M = 0.14;
/** `localStorage` keys: the lift's own lever, and the planner's (THE LEVER IS TOTAL — the classic room has no shaft). */
export const LIFT_LEVER_KEY = 'atlas:interior-lift';
const PLANNER_LEVER = 'atlas:interior-planner';

export interface LiftShaft {
  /** The shaft cell, building-local metres — the planner's candidate rect, the same on every floor. */
  rect: { x0: number; z0: number; x1: number; z1: number };
  /** The perimeter wall it stands against, and the side its door opens to (into the room). */
  wall: 'west' | 'north';
  door: 'east' | 'south';
  /** Where a rider stands to call it (the apron's middle) and the yaw that faces the door (the plans' convention). */
  call: { x: number; z: number; facing: number };
  /** Per floor: the PLUG's index among the shell's collide boxes — the visit names that collider `int:<id>:<n>`. */
  plugs: number[];
}

/** A veto asked of the shell's OWNER while it cuts the room (the lift module installs it on the client): true = this
 *  building's plan stands furniture in the reserve, so the shell is cut WITHOUT a shaft — exactly the pre-lift shell.
 *  Never set on the server or in a worker (the shaft is there; the planner's output does not depend on it). */
export type LiftShaftVeto = (key: string | undefined, w: number, h: number, d: number) => boolean;
let liftVeto: LiftShaftVeto | null = null;
export function setLiftShaftVeto(fn: LiftShaftVeto | null): void { liftVeto = fn; }

let liftOverride: boolean | null = null;
let liftLeverAt = -1e9, liftLeverOn = true;
/** Force the shaft on / off (harnesses, beds); `null` = follow the lever. */
export function setLiftShaft(on: boolean | null): void { liftOverride = on; liftLeverAt = -1e9; }
/** Is the shaft built? Default ON; `localStorage atlas:interior-lift = 'off'` — or the planner's lever — restores the
 *  shell exactly as it was before slice L. Where there is no `localStorage` (node, a worker, the server) it is on: the
 *  planner's output does not depend on it (the gate plans both ways and compares digests). */
export function liftShaftEnabled(): boolean {
  if (liftOverride !== null) return liftOverride;
  const now = typeof performance !== 'undefined' ? performance.now() : 0;
  if (now - liftLeverAt < 1000) return liftLeverOn;
  liftLeverAt = now;
  try {
    const ls = (globalThis as unknown as { localStorage?: { getItem(k: string): string | null } }).localStorage;
    liftLeverOn = !ls || (ls.getItem(LIFT_LEVER_KEY) !== 'off' && ls.getItem(PLANNER_LEVER) !== 'off');
  } catch { liftLeverOn = true; }
  return liftLeverOn;
}

/**
 * THE CLEAR-LANDING RULE (the interiors programme, slice U, 2026-10-02). The classic back-wall shaft stands 2.0 m east of
 * the stair's axis. In a shell shallower than ~9.6 m the stair run takes all the depth but 1.2 m at each end, so the
 * shaft (1.6 m deep) reaches down BESIDE the stair's guards: the landing's only way into the room is the 0.80 m between
 * the run's guard rail and the shaft, and then 0.62 m between the opening's low rail and the shaft's corner — too
 * narrow for a body with its air (0.70 m), so every floor above the ground read as sealed to the planner (measured:
 * 3,423 of the 7,100 baked towers; slice L's census of "a legacy storey stands furniture in the shaft" is this).
 * `clear` keeps `LIFT_WAY_OUT_M` between the shaft's west face and whichever stair guard its south face comes down
 * beside — the shaft stands up to 0.4 m further east, and nowhere else changes.
 *
 *   classic   the rule as slice L wrote it — every plan at a layout below THE STOREYS REVISION was made round it, and
 *             every DEED's shell keeps it at every layout (a deed can be PINNED to an older layout; its shell is cut
 *             without knowing which — the one deed type the rule would move is the mining facility);
 *   clear     every other shell (a settlement building, a preset, a marker — never pinned, always planned at the
 *             current layout) once `LIFT_SHAFT_RULE_LIVE` says so. The planner asks for the rule BY NAME
 *             (`opts.liftRule`), so a plan at an older layout still gets the shell it was made for.
 */
export type LiftShaftRule = 'classic' | 'clear';
/** The rule a NON-DEED shell is cut with when its caller names none — flipped with the current layout revision
 *  (`interior_wiring.CURRENT_LAYOUT_REVISION` ≥ `interior_descriptor.STOREYS_LAYOUT_REVISION` ⇔ 'clear'; the gate pins it).
 *  'clear' since THE STOREYS REVISION went live (slice U, 2026-10-02). */
export const LIFT_SHAFT_RULE_LIVE: LiftShaftRule = 'clear';
let liftRuleOverride: LiftShaftRule | null = null;
/** THE STOREYS LEVER's half on the shell: the client (`interior_wiring`, from `atlas:interior-storeys`) sets 'classic'
 *  to stand every shaft where layout 2 stood it; null = `LIFT_SHAFT_RULE_LIVE`. Never set on the server. */
export function setLiftShaftRuleLive(rule: LiftShaftRule | null): void { liftRuleOverride = rule; }
export function liftShaftRuleFor(key: string | undefined): LiftShaftRule { return key && key.startsWith('deed:') ? 'classic' : (liftRuleOverride ?? LIFT_SHAFT_RULE_LIVE); }
/** The clear way kept between the shaft and a stair guard it stands beside (m): a body and its air (0.70 m) and two
 *  cells of the planner's 0.1 m route raster — "a 0.9 m gap is always found". */
export const LIFT_WAY_OUT_M = 0.9;

/**
 * Where a shell's lift shaft stands, or null (under three floors, or no legal place). The planner's rule in the
 * planner's own integer millimetres: (1) against the west wall, north of the stair run, when the room is that deep;
 * (2) else the first clear place on the back wall east of the stair zone. Never in the door corridor or the stair zone
 * (law 22's centre rule, asked of the shaft's and the apron's corners and centres), never on a deed's console ground.
 * `rule` 'clear' adds THE CLEAR-LANDING RULE to (2).
 */
export function liftShaftFor(shell: { floors: number; hw: number; hd: number; stair: { x: number; z: number; w: number; d: number } }, deed: boolean, rule: LiftShaftRule = 'classic'): Omit<LiftShaft, 'plugs'> | null {
  if (shell.floors < 3) return null;
  const mm = (m: number): number => Math.round(m * 1000);
  const snap = (v: number): number => Math.round(v / 10) * 10;
  type Bx = { x0: number; z0: number; x1: number; z1: number };
  const hw = mm(shell.hw), hd = mm(shell.hd), L = mm(LIFT_SHAFT_M), A = mm(LIFT_APRON_M);
  const st = shell.stair;
  // the console's keep-out on a deed's ground (structure_access.consoleSeat + furnish_packs.CONSOLE_KEEP_*, mirrored)
  let keep: Bx | null = null;
  if (deed) {
    const cz = shell.hd - 0.75;
    let cx = 0;
    if (Math.abs(cx - st.x) <= st.w / 2 + 0.6 && Math.abs(cz - st.z) <= st.d / 2 + 0.6) {
      const right = Math.min(shell.hw - 0.7, st.x + st.w / 2 + 1.0), left = Math.max(-shell.hw + 0.7, st.x - st.w / 2 - 1.0);
      cx = (shell.hw - right) >= (left + shell.hw) ? right : left;
    }
    keep = { x0: mm(cx - 0.9), x1: mm(cx + 0.9), z0: mm(cz - 1.9), z1: mm(cz + 0.5) };
  }
  // interior_plans.slotClear(frame, x, z, 0): off the door corridor and the stair zone, inside the room
  const legal = (xm: number, zm: number): boolean => {
    const x = xm / 1000, z = zm / 1000;
    if (Math.abs(x) < 1.4 && z < -shell.hd + 3.4) return false;
    if (x > st.x - 2.0 && x < st.x + 2.0 && z > st.z - st.d / 2 - 1.8 && z < st.z + st.d / 2 + 1.8) return false;
    return Math.abs(x) < shell.hw && Math.abs(z) < shell.hd;
  };
  const meets = (a: Bx, b: Bx): boolean => a.x0 < b.x1 && a.x1 > b.x0 && a.z0 < b.z1 && a.z1 > b.z0;
  const clear = (box: Bx, apron: Bx): boolean => {
    for (const q of [box, apron]) {
      if (keep && meets(q, keep)) return false;
      for (const [x, z] of [[q.x0, q.z0], [q.x1, q.z0], [q.x0, q.z1], [q.x1, q.z1], [(q.x0 + q.x1) / 2, (q.z0 + q.z1) / 2]]) {
        if (!legal(Math.max(-hw + 1, Math.min(hw - 1, x)), Math.max(-hd + 1, Math.min(hd - 1, z)))) return false;
      }
    }
    return true;
  };
  const out = (b: Bx): LiftShaft['rect'] => ({ x0: b.x0 / 1000, z0: b.z0 / 1000, x1: b.x1 / 1000, z1: b.z1 / 1000 });
  {
    const z0 = snap(mm(st.z + st.d / 2 + 1.8) + 10);
    const box: Bx = { x0: -hw, x1: -hw + L, z0, z1: z0 + L };
    const apron: Bx = { x0: box.x1, x1: box.x1 + A, z0: box.z0, z1: box.z1 };
    if (box.z1 <= hd && clear(box, apron)) return { rect: out(box), wall: 'west', door: 'east', call: { x: (box.x1 + A / 2) / 1000, z: (box.z0 + box.z1) / 2000, facing: -Math.PI / 2 } };
  }
  // THE CLEAR-LANDING RULE: the stair's guards as `interiorSpec` builds them — the run's guard rail (0.4 m thick, its
  // east face 1.21 m off the axis, as long as the run) and the opening's low rail (0.6 m thick, east face 1.5 m off,
  // ending 0.75 m short of the run's north end). A shaft whose south face comes within `LIFT_WAY_OUT_M` of a guard's
  // north end keeps `LIFT_WAY_OUT_M` east of that guard's face.
  const W = mm(LIFT_WAY_OUT_M);
  const railX = mm(st.x + st.w / 2 + 0.41), railZ = mm(st.z + st.d / 2), lowX = mm(st.x + st.w / 2 + 0.7), lowZ = mm(st.z + st.d / 2 - 0.75);
  const wayOut = (box: Bx): boolean => (box.z0 >= railZ + W || box.x0 >= railX + W) && (box.z0 >= lowZ + W || box.x0 >= lowX + W);
  for (let x0 = snap(mm(st.x + 2.0) + 10); x0 + L <= hw; x0 += 50) {
    const box: Bx = { x0, x1: x0 + L, z0: hd - L, z1: hd };
    const apron: Bx = { x0, x1: x0 + L, z0: box.z0 - A, z1: box.z0 };
    if (rule === 'clear' && !wayOut(box)) continue;
    if (clear(box, apron)) return { rect: out(box), wall: 'north', door: 'south', call: { x: (box.x0 + box.x1) / 2000, z: (box.z0 - A / 2) / 1000, facing: 0 } };
  }
  return null;
}

/** Exclusions: never-enterable structure classes (industry, small props). */
const EXCLUDE = /mine|extract|rig|derrick|drill|antenna|lamp|crate|kiosk|stall|silo|tank|pipe|wall|gate|fence|bench|fountain|statue|ship|pad|sign|arch|pylon|bridge|tower_comm|turret|barricade/;
/** Living/civic/hall vocabulary — the enterable classes. Covers the legacy
 *  human-readable prefab names (hab_pod, courtyard_home, villa_luxury,
 *  modular_duplex, council_chamber, terrace_row_home, academy …); the opaque
 *  civ-pack keys (`civa_scifi5`) classify via the caller's `allow` set instead
 *  (ENTERABLE_CIV_KEYS — derived from the pack PROGRAM tables). */
const INCLUDE = /habitat|hab|home|tower|hall|civic|dwell|house|villa|duplex|terrace|chamber|council|academy|apart|dome|spire|manor|lodge|barrack|temple|sanctum|guild|residen|quarter|admin|estate|monolith|complex|block/;

/** The three real-room floors (m): anything smaller has no room for a shell. Settlement buildings, player deeds,
 *  Edit Mode markers. */
export const SIZE_FLOOR = {
  settlement: { w: 7, d: 7, h: 4.6 },
  deed: { w: 6, d: 6, h: 4.2 },
  marker: { w: 3, d: 3, h: 2.6 },
} as const;

/** Is this building enterable? THE CAPABILITY TABLE first (`interior_capability.ts`, 2026-10-01: every civ-pack key
 *  and the port keys are classified on purpose); the key vocabulary (or the caller's allow-set for opaque pack keys)
 *  only for keys the table does not name — the legacy human-readable prefab names. Then a real-room size floor.
 *  Admission is what it was before the table: `verify:interior-coverage` runs the old predicate beside this one. */
export function interiorEligible(key: string, w: number, h: number, d: number, allow?: ReadonlySet<string>): boolean {
  const e = settlementCapability(key);
  if (e) return admits(e.capability) && w >= 7 && d >= 7 && h >= 4.6;
  const k = key.toLowerCase();
  if (EXCLUDE.test(k)) return false;
  if (!INCLUDE.test(k) && !allow?.has(key)) return false;
  return w >= 7 && d >= 7 && h >= 4.6;
}

/** Deed (player-placed) eligibility: the capability table (all 37 catalog types), else the industry exclusions for a
 *  type it does not name; then the size gate. A player's house or guild hall enters; an extractor or a wall never does.
 *  (The server's walking grid carries the same rule on the footprint table: `player_site_grid.walkInDoor`.) */
export function interiorEligibleDeed(structureType: string, w: number, h: number, d: number): boolean {
  const e = deedCapability(structureType);
  if (e ? !admits(e.capability) : EXCLUDE.test(structureType.toLowerCase())) return false;
  return w >= 6 && d >= 6 && h >= 4.2;
}

/** An Edit Mode marker: no vocabulary, the smallest floor — the owner's flag is the override. */
export function interiorEligibleMarker(w: number, h: number, d: number): boolean {
  return w >= 3 && h >= 2.6 && d >= 3;
}

// ── Admission WITH its reason (the coverage export, `__interior`, a future door tooltip) ─────────────────────────────
// The same decisions as the three predicates above, said out loud: which rule decided, the class, and — when the
// class admits — which dimension the size floor refused. The gate pins `ok` to the boolean predicates.

export interface Admission {
  ok: boolean;
  /** The capability table named the key, or the legacy vocabulary classified it. */
  by: 'table' | 'vocabulary';
  /** The class (on the vocabulary path: an exclusion word = equipmentOnly, an inclusion word or the allow-set =
   *  habitable, no word at all = sealed). */
  capability: InteriorCapability;
  /** Machine-readable: the class for a table key; `exclude:<word>` | `include:<word>` | `allow-set` | `no-word`. */
  rule: string;
  /** What the size floor refused (`d 5.99 < 6`), or null when it passed or was never reached. */
  size: string | null;
  /** One sentence. */
  why: string;
}

function sizeVerdict(f: { w: number; d: number; h: number }, w: number, h: number, d: number): string | null {
  const bad: string[] = [];
  if (!(w >= f.w)) bad.push(`w ${w.toFixed(2)} < ${f.w}`);
  if (!(d >= f.d)) bad.push(`d ${d.toFixed(2)} < ${f.d}`);
  if (!(h >= f.h)) bad.push(`h ${h.toFixed(2)} < ${f.h}`);
  return bad.length ? bad.join(' · ') : null;
}

function admission(by: Admission['by'], capability: InteriorCapability, rule: string, reason: string, floor: { w: number; d: number; h: number }, w: number, h: number, d: number): Admission {
  if (!admits(capability)) return { ok: false, by, capability, rule, size: null, why: `${capability}: ${reason}` };
  const size = sizeVerdict(floor, w, h, d);
  return { ok: size === null, by, capability, rule, size, why: size === null ? `${capability}: ${reason}` : `${capability} (${reason}) but too small: ${size}` };
}

/** `interiorEligible`, with its reason. */
export function settlementAdmission(key: string, w: number, h: number, d: number, allow?: ReadonlySet<string>): Admission {
  const f = SIZE_FLOOR.settlement;
  const e = settlementCapability(key);
  if (e) return admission('table', e.capability, e.capability, e.reason, f, w, h, d);
  const k = key.toLowerCase();
  const x = EXCLUDE.exec(k);
  if (x) return admission('vocabulary', 'equipmentOnly', `exclude:${x[0]}`, `the exclusion word "${x[0]}"`, f, w, h, d);
  const i = INCLUDE.exec(k);
  if (i) return admission('vocabulary', 'habitable', `include:${i[0]}`, `the vocabulary word "${i[0]}"`, f, w, h, d);
  if (allow?.has(key)) return admission('vocabulary', 'habitable', 'allow-set', 'in the caller\'s allow-set', f, w, h, d);
  return admission('vocabulary', 'sealed', 'no-word', 'no vocabulary word names it and it is in no allow-set', f, w, h, d);
}

/** `interiorEligibleDeed`, with its reason. */
export function deedAdmission(structureType: string, w: number, h: number, d: number): Admission {
  const f = SIZE_FLOOR.deed;
  const e = deedCapability(structureType);
  if (e) return admission('table', e.capability, e.capability, e.reason, f, w, h, d);
  const x = EXCLUDE.exec(structureType.toLowerCase());
  if (x) return admission('vocabulary', 'equipmentOnly', `exclude:${x[0]}`, `the exclusion word "${x[0]}"`, f, w, h, d);
  return admission('vocabulary', 'habitable', 'no-word', 'an unknown deed type with no exclusion word', f, w, h, d);
}

/** `interiorEligibleMarker`, with its reason. */
export function markerAdmission(w: number, h: number, d: number): Admission {
  return admission('table', 'habitable', 'marker', 'an owner flagged it enterable in Edit Mode', SIZE_FLOOR.marker, w, h, d);
}

export interface IntBox {
  x: number; y: number; z: number;      // center, building-local (y from floor 0)
  w: number; h: number; d: number;
  mat: 'wall' | 'floor' | 'stair' | 'trim';
  collide: boolean;
  /** false = collider-only (no mesh): the airtight wall sheets + stair ramp. */
  render?: boolean;
}
export interface IntPane {
  x: number; y: number; z: number;      // center
  w: number; h: number;                 // pane size in its wall plane
  axis: 'x' | 'z';                      // wall normal axis
}

export interface InteriorSpec {
  floors: number;
  hw: number; hd: number;               // interior half extents
  boxes: IntBox[];
  panes: IntPane[];
  /** Door center on the local -Z face (walk-in gap), building-local. */
  door: { x: number; z: number };
  /** Stair region (kept clear of slabs) per floor. */
  stair: { x: number; z: number; w: number; d: number };
  /** THE LIFT SHAFT (slice L): where it stands and its plugs — null / absent when the shell has none. */
  lift?: LiftShaft | null;
}

/**
 * Derive the full interior: floors, windowed perimeter walls, a door gap on
 * the -Z face, straight stair runs, slab openings above them. Deterministic
 * from the building dimensions alone.
 */
export function interiorSpec(w: number, h: number, d: number, key?: string, opts?: { lift?: boolean; liftRule?: LiftShaftRule }): InteriorSpec {
  // Core clamp: some cooked landmarks carry degenerate km-scale bboxes (the
  // civa_ship class — a "civic hall" measured 1170×1445 m and grew 19k window
  // panes). The walkable interior occupies the HEART of such a monument: a
  // bounded core keeps pane/collider counts sane and rooms human-scaled.
  const hw = Math.min(w, 60) / 2 - WALL_IN;
  const hd = Math.min(d, 60) / 2 - WALL_IN;
  const floors = Math.max(1, Math.min(MAX_FLOORS, Math.floor((h - 1.6) / FLOOR_H)));
  const boxes: IntBox[] = [];
  const panes: IntPane[] = [];

  // Stair run: along the −X (west) wall, climbing toward +Z. Kept identical per floor.
  const stair = { x: -hw + 1.1, z: 0, w: 1.6, d: Math.min(2 * hd - 2.4, FLOOR_H * 1.9) };
  const STEPS = 8;            // visible treads
  const RAMP_SLICES = 24;     // collide-only ramp under them (0.14 m rises — the
                              // 0.425 m per-step colliders fought the KCC autostep)
  const rise = FLOOR_H / STEPS;
  const tread = stair.d / STEPS;

  // THE LIFT SHAFT (slice L): decided from the shell alone, like everything here.
  // `opts.lift`: false = never; true = wherever it is legal (the lever still parks it); absent = the lever AND the veto.
  const wantShaft = floors >= 3 && opts?.lift !== false && liftShaftEnabled() && (opts?.lift === true || !liftVeto || !liftVeto(key, w, h, d));
  // `opts.liftRule`: the planner names the rule its layout was made round; anyone else takes the key's (`liftShaftRuleFor`).
  const shaft = wantShaft ? liftShaftFor({ floors, hw, hd, stair }, !!key && key.startsWith('deed:'), opts?.liftRule ?? liftShaftRuleFor(key)) : null;
  /** A slab strip — cut round the shaft's cell where they meet (the opening a cab rides through). */
  const slab = (x0: number, x1: number, z0: number, z1: number, y: number, h: number): void => {
    const r = shaft?.rect;
    if (!r || x1 <= r.x0 || x0 >= r.x1 || z1 <= r.z0 || z0 >= r.z1) {
      boxes.push({ x: (x0 + x1) / 2, y, z: (z0 + z1) / 2, w: x1 - x0, h, d: z1 - z0, mat: 'floor', collide: true });
      return;
    }
    const piece = (ax0: number, ax1: number, az0: number, az1: number): void => {
      if (ax1 - ax0 > 0.02 && az1 - az0 > 0.02) boxes.push({ x: (ax0 + ax1) / 2, y, z: (az0 + az1) / 2, w: ax1 - ax0, h, d: az1 - az0, mat: 'floor', collide: true });
    };
    piece(x0, Math.min(x1, r.x0), z0, z1);                                     // west of the cell
    piece(Math.max(x0, r.x1), x1, z0, z1);                                     // east of it
    piece(Math.max(x0, r.x0), Math.min(x1, r.x1), z0, Math.min(z1, r.z0));     // before it
    piece(Math.max(x0, r.x0), Math.min(x1, r.x1), Math.max(z0, r.z1), z1);     // behind it
  };

  // Slab thickness: THICK downward (top edge unchanged) — the same thin-
  // collider mid-plane defense as the walls: a 0.16 m slab let the capsule
  // depenetrate to the underside and drop a floor (field bug 2026-08-14).
  const t = 0.5;
  for (let f = 0; f < floors; f++) {
    const y0 = f * FLOOR_H;
    // Floor slab (floor 0 sits on the pad; still gets a thin finish slab).
    if (f === 0) {
      boxes.push({ x: 0, y: 0.04, z: 0, w: 2 * hw, h: 0.08, d: 2 * hd, mat: 'floor', collide: true });
    } else {
      // Slab with the stair opening: two strips beside/around the stairwell.
      const ox0 = stair.x - stair.w / 2 - 0.1;  // opening x range
      const ox1 = stair.x + stair.w / 2 + 0.1;
      const oz0 = stair.z - stair.d / 2 - 0.1;
      const oz1 = stair.z + stair.d / 2 + 0.55; // landing lip
      if (shaft) {
        // (slice L) the same four strips, each cut round the shaft's cell
        slab(ox1, hw, -hd, hd, y0 - t / 2, t);
        if (ox0 > -hw + 0.05) slab(-hw, ox0, -hd, hd, y0 - t / 2, t);
        if (oz0 > -hd + 0.05) slab(ox0, ox1, -hd, oz0, y0 - t / 2, t);
        if (oz1 < hd - 0.05) slab(ox0, ox1, oz1, hd, y0 - t / 2, t);
      } else {
      // Right of the opening (most of the floor).
      boxes.push({ x: (ox1 + hw) / 2, y: y0 - t / 2, z: 0, w: hw - ox1, h: t, d: 2 * hd, mat: 'floor', collide: true });
      // Left sliver.
      if (ox0 > -hw + 0.05) boxes.push({ x: (-hw + ox0) / 2, y: y0 - t / 2, z: 0, w: ox0 + hw, h: t, d: 2 * hd, mat: 'floor', collide: true });
      // Front + back strips within the stair column.
      if (oz0 > -hd + 0.05) boxes.push({ x: (ox0 + ox1) / 2, y: y0 - t / 2, z: (-hd + oz0) / 2, w: ox1 - ox0, h: t, d: oz0 + hd, mat: 'floor', collide: true });
      if (oz1 < hd - 0.05) boxes.push({ x: (ox0 + ox1) / 2, y: y0 - t / 2, z: (oz1 + hd) / 2, w: ox1 - ox0, h: t, d: hd - oz1, mat: 'floor', collide: true });
      }
      // Stairwell SIDE railings ON this floor along the opening (the naked
      // hole was the "reached floor 2, dropped to floor 1" path: sidestep
      // under the upper ramp, fall through the slab opening onto the lower
      // run). BOTH ends stay open — in a stacked stairwell the -Z end is the
      // departure onto the next run up and the +Z end is the arrival edge
      // (it lands on the lower ramp's top step, never a real fall).
      // Rails guard only the DEEP part of the hole: they used to run 0.15 m
      // PAST the slab edge, walling the arrival landing into a 5 cm corridor
      // ("small barrier blocking the entrance to the next floor"). The last
      // ~1.3 m of the opening is the arrival — falling "in" there just steps
      // onto the ramp top. 0.6 thick (the thin-collider law).
      const railH = 1.05, railT = 0.6;
      const railEnd = oz1 - 1.3;
      const railD = railEnd - (oz0 - 0.15);
      boxes.push({ x: ox0 - railT / 2, y: y0 + railH / 2, z: (oz0 - 0.15 + railEnd) / 2, w: railT, h: railH, d: railD, mat: 'trim', collide: true });
      boxes.push({ x: ox1 + railT / 2, y: y0 + railH / 2, z: (oz0 - 0.15 + railEnd) / 2, w: railT, h: railH, d: railD, mat: 'trim', collide: true });
    }
    // Stairs up from this floor (not from the top floor). SOLID to the floor
    // (2026-08-14 field fixes): thin treads/slices carried reachable
    // mid-planes — at walking speed a reconcile nudge pushed the capsule
    // THROUGH the ramp (resolved downward, wedged under it, then the unstick
    // ejected the player). Every tread and every collide slice now extends
    // down to the run's base: no underside exists, and it reads as a real
    // closed-stringer staircase.
    if (f < floors - 1) {
      // Depth is BOUNDED at 0.8 m (not solid to the run base): deep enough
      // that no reconcile shove (≤0.3 m) can reach a slice's mid-plane, but
      // shallow enough that the NEXT run up (stacked in the same column)
      // doesn't ceiling the run below — fully-solid stairs put a 3.4 m
      // ceiling over the climber's head and wedged them mid-ramp.
      const DEPTH = 0.8;
      for (let s2 = 0; s2 < STEPS; s2++) {
        const top = rise * (s2 + 1);
        const h = Math.min(top, DEPTH);
        boxes.push({
          x: stair.x,
          y: y0 + top - h / 2,
          z: stair.z - stair.d / 2 + tread * (s2 + 0.5),
          w: stair.w, h, d: tread,
          mat: 'stair', collide: false,
        });
      }
      const sliceRise = FLOOR_H / RAMP_SLICES;
      const sliceTread = stair.d / RAMP_SLICES;
      for (let s2 = 0; s2 < RAMP_SLICES; s2++) {
        const top = sliceRise * (s2 + 1);
        const h = Math.min(top, DEPTH);
        boxes.push({
          x: stair.x,
          y: y0 + top - h / 2,
          z: stair.z - stair.d / 2 + sliceTread * (s2 + 0.5),
          w: stair.w, h, d: sliceTread,
          mat: 'stair', collide: true, render: false,
        });
      }
      // LANDING PLATFORM: the slab opening's lip left a 0.55 m HOLE between
      // the ramp top and the next floor's edge ("small gap between the
      // stairs and the second floor" — players fell straight through it).
      // A solid platform flush with both closes it, overlapping the slab.
      const lz0 = stair.z + stair.d / 2 - 0.15;
      const lz1 = stair.z + stair.d / 2 + 0.75;
      boxes.push({ x: stair.x, y: y0 + FLOOR_H - 0.25, z: (lz0 + lz1) / 2, w: stair.w, h: 0.5, d: lz1 - lz0, mat: 'floor', collide: true });
      // Guard rail along the open side of the run.
      // 0.4 thick — the 0.1 rail was the building's last THIN collider and the
      // mid-ramp glitch point (reconcile nudges crossed its 5 cm mid-plane).
      boxes.push({ x: stair.x + stair.w / 2 + 0.21, y: y0 + FLOOR_H / 2, z: stair.z, w: 0.4, h: FLOOR_H, d: stair.d, mat: 'trim', collide: true });
    }
    // Perimeter walls with window bands (all four faces; door gap on -Z, floor 0).
    const wallY = y0;
    const face = (axis: 'x' | 'z', sign: 1 | -1): void => {
      const len = axis === 'z' ? 2 * hw : 2 * hd; // wall length across
      const wallPos = axis === 'z' ? sign * hd : sign * hw;
      const doorHere = f === 0 && axis === 'z' && sign === -1;
      // Below-sill + above-header bands (full length, minus the door gap).
      const segs: Array<{ c: number; l: number }> = [];
      if (doorHere) {
        const g = DOOR_W / 2 + 0.1;
        segs.push({ c: -(len / 2 + g) / 2 + 0, l: len / 2 - g }); // left of door
        segs.push({ c: (len / 2 + g) / 2, l: len / 2 - g });      // right of door
      } else {
        segs.push({ c: 0, l: len });
      }
      for (const sg of segs) {
        if (sg.l < 0.2) continue;
        const bx = axis === 'z' ? sg.c : wallPos;
        const bz = axis === 'z' ? wallPos : sg.c;
        const bw = axis === 'z' ? sg.l : 0.16;
        const bd = axis === 'z' ? 0.16 : sg.l;
        // Visual bands only — collision comes from the per-face airtight
        // sheets below (the collide-true sill band left the window band OPEN
        // above 1 m: players on the stairs walked through the glass and fell
        // out of the building).
        boxes.push({ x: bx, y: wallY + SILL_H / 2, z: bz, w: bw, h: SILL_H, d: bd, mat: 'wall', collide: false });
        boxes.push({ x: bx, y: wallY + (HEADER_H + FLOOR_H) / 2, z: bz, w: bw, h: FLOOR_H - HEADER_H, d: bd, mat: 'wall', collide: false });
      }
      if (doorHere) {
        // Lintel over the door.
        boxes.push({ x: 0, y: wallY + (DOOR_H + FLOOR_H) / 2, z: wallPos, w: DOOR_W + 0.3, h: FLOOR_H - DOOR_H, d: 0.16, mat: 'wall', collide: false });
      }
      // Window band: piers + panes between sill and header.
      const bandH = HEADER_H - SILL_H;
      const paneW = 1.7, pierW = 0.5;
      const usable = doorHere ? len / 2 - DOOR_W / 2 - 0.3 : len;
      const count = Math.max(1, Math.floor(usable / (paneW + pierW)));
      const span = count * (paneW + pierW) - pierW;
      const runs = doorHere ? [-(len / 4 + DOOR_W / 4), (len / 4 + DOOR_W / 4)] : [0];
      for (const runC of runs) {
        const start = runC - span / 2;
        for (let i = 0; i <= count; i++) {
          // Piers (collide: keeps bodies in; panes stay pure glass).
          const pc = start + i * (paneW + pierW) - pierW / 2;
          if (i > 0 || !doorHere) {
            const bx = axis === 'z' ? pc : wallPos;
            const bz = axis === 'z' ? wallPos : pc;
            boxes.push({ x: bx, y: wallY + SILL_H + bandH / 2, z: bz, w: axis === 'z' ? pierW : 0.16, h: bandH, d: axis === 'z' ? 0.16 : pierW, mat: 'wall', collide: false });
          }
          if (i < count) {
            const cc = start + i * (paneW + pierW) + paneW / 2;
            panes.push({
              x: axis === 'z' ? cc : wallPos,
              y: wallY + SILL_H + bandH / 2,
              z: axis === 'z' ? wallPos : cc,
              w: paneW, h: bandH,
              axis,
            });
          }
        }
      }
      // Ceiling trim strip (warm interior read — the baked-materials law).
      boxes.push({
        x: axis === 'z' ? 0 : wallPos * 0.98, y: wallY + FLOOR_H - 0.18,
        z: axis === 'z' ? wallPos * 0.98 : 0,
        w: axis === 'z' ? len * 0.92 : 0.06, h: 0.08, d: axis === 'z' ? 0.06 : len * 0.92,
        mat: 'trim', collide: false,
      });
    };
    face('z', -1); face('z', 1); face('x', -1); face('x', 1);
  }
  // ── Airtight perimeter (collide-only, invisible): one full-height sheet per
  // face so NOTHING leaves the building except through the door gap — the
  // window band is glass to the eye and wall to the body.
  //
  // THICKNESS is the tunneling defense (2026-08-14 field bug: reconcile
  // teleport-nudges embedded the capsule in a 0.25 m sheet and depenetration
  // resolved it to the FAR side — ejected out of the tower mid-stairs; thin
  // colliders depenetrate to the wrong side by construction). The sheets are
  // 1.6 m thick and extend OUTWARD — the inner face stays at the wall plane,
  // and no capsule can ever reach a sheet's mid-plane from inside. ──
  const T = 1.6;
  const IN = 0.125;                 // inward overlap with the visual wall bands
  const OUT = T / 2 - IN;           // center offset outward from the wall plane
  const H = floors * FLOOR_H;
  // +Z / ±X faces: single full sheets (widened so corners overlap solidly).
  boxes.push({ x: 0, y: H / 2, z: hd + OUT, w: 2 * hw + 2 * T, h: H, d: T, mat: 'wall', collide: true, render: false });
  boxes.push({ x: -hw - OUT, y: H / 2, z: 0, w: T, h: H, d: 2 * hd + 2 * T, mat: 'wall', collide: true, render: false });
  boxes.push({ x: hw + OUT, y: H / 2, z: 0, w: T, h: H, d: 2 * hd + 2 * T, mat: 'wall', collide: true, render: false });
  // -Z face: two sheets flanking the door gap + a transom above it (the
  // doorway becomes a short solid tunnel through the thick wall).
  const g = DOOR_W / 2 + 0.1;
  const sideW = hw - g;
  if (sideW > 0.1) {
    boxes.push({ x: -(g + sideW / 2), y: H / 2, z: -hd - OUT, w: sideW, h: H, d: T, mat: 'wall', collide: true, render: false });
    boxes.push({ x: g + sideW / 2, y: H / 2, z: -hd - OUT, w: sideW, h: H, d: T, mat: 'wall', collide: true, render: false });
  }
  boxes.push({ x: 0, y: DOOR_H + (H - DOOR_H) / 2, z: -hd - OUT, w: 2 * g, h: H - DOOR_H, d: T, mat: 'wall', collide: true, render: false });
  // Roof cap over the top floor (thick upward; underside unchanged).
  boxes.push({ x: 0, y: floors * FLOOR_H + t / 2, z: 0, w: 2 * hw, h: t, d: 2 * hd, mat: 'floor', collide: true });

  // ── THE LIFT SHAFT (slice L): per storey two side walls, the wall over the landing door, a threshold strip, and the
  // PLUG — a collide-only box filling the cell. All of it inside the candidate's rect (see the block at the top).
  // (u runs along the door wall, v from the door plane to the perimeter wall.)
  let lift: LiftShaft | null = null;
  if (shaft) {
    const r = shaft.rect, west = shaft.wall === 'west';
    const at = (u0: number, u1: number, v0: number, v1: number): { x: number; z: number; w: number; d: number } => west
      ? { x: r.x1 - (v0 + v1) / 2, z: r.z0 + (u0 + u1) / 2, w: v1 - v0, d: u1 - u0 }
      : { x: r.x0 + (u0 + u1) / 2, z: r.z0 + (v0 + v1) / 2, w: u1 - u0, d: v1 - v0 };
    const S = LIFT_SHAFT_M, SW = LIFT_SIDE_WALL_M;
    const plugs: number[] = [];
    for (let f = 0; f < floors; f++) {
      const y0 = f * FLOOR_H;
      boxes.push({ ...at(0, SW, 0, S), y: y0 + FLOOR_H / 2, h: FLOOR_H, mat: 'wall', collide: true });
      boxes.push({ ...at(S - SW, S, 0, S), y: y0 + FLOOR_H / 2, h: FLOOR_H, mat: 'wall', collide: true });
      boxes.push({ ...at(SW, S - SW, 0, LIFT_FRONT_M), y: y0 + (LIFT_DOOR_H + FLOOR_H) / 2, h: FLOOR_H - LIFT_DOOR_H, mat: 'wall', collide: false });
      boxes.push({ ...at(SW, S - SW, 0, LIFT_FRONT_M), y: (f === 0 ? 0.08 : y0) + 0.012, h: 0.024, mat: 'trim', collide: false });
      plugs.push(boxes.reduce((n, b) => n + (b.collide ? 1 : 0), 0)); // the plug's place among the collide boxes
      boxes.push({ ...at(0, S, 0, S), y: y0 + FLOOR_H / 2, h: FLOOR_H, mat: 'wall', collide: true, render: false });
    }
    lift = { ...shaft, plugs };
  }

  // ── The guild hall's UNIQUE great hall (2026-08-29): twin column rows down
  // the nave, a raised dais before the far wall, and hanging banners — all
  // structural boxes INSIDE the proven shell (door corridor, stair approach,
  // and every KCC collider law untouched). Furniture rides the bespoke
  // furnishGuildHall plan in BuildingInteriorModule. ──
  if (key === 'deed:guild_hall') {
    const colX = Math.min(hw * 0.45, hw - 2.2);
    if (colX > 2.2) {
      for (let z = -hd + 4.2; z <= hd - 5.2; z += 4.5) {
        for (const sx of [-1, 1]) {
          // Skip any column that would crowd the stair column on the -X side.
          if (sx * colX > stair.x - 2.0 && sx * colX < stair.x + 2.0) continue;
          boxes.push({ x: sx * colX, y: FLOOR_H / 2, z, w: 0.55, h: FLOOR_H, d: 0.55, mat: 'trim', collide: true });
          // A hanging banner off each column's nave face (render-only cloth).
          boxes.push({ x: sx * (colX - 0.36), y: (FLOOR_H - 0.2 + 1.5) / 2, z, w: 0.06, h: FLOOR_H - 1.7, d: 1.1, mat: 'wall', collide: false });
        }
      }
    }
    // The dais: a low stage before the +Z wall (the charter is signed here).
    const daisW = Math.min(2 * hw * 0.55, 14);
    boxes.push({ x: 0, y: 0.175, z: hd - 1.65, w: daisW, h: 0.35, d: 2.6, mat: 'stair', collide: true });
    // A step lip so the KCC walks up without a hop.
    boxes.push({ x: 0, y: 0.085, z: hd - 3.1, w: daisW * 0.7, h: 0.17, d: 0.6, mat: 'stair', collide: true });
    // The great banner behind the dais (render-only, off the wall plane).
    boxes.push({ x: 0, y: (FLOOR_H - 0.15 + 0.7) / 2, z: hd - 0.32, w: Math.min(daisW * 0.6, 8), h: FLOOR_H - 0.85, d: 0.06, mat: 'wall', collide: false });
  }

  // ── The Merchant Hive's premium stores (2026-08-29): an elegant RETAIL
  // floor — perimeter display shelving, twin pedestal aisles, and a service
  // counter before the back wall. Same shell laws; the door corridor and the
  // stair approach stay clear. ──
  if (key === 'hive:store') {
    // Perimeter shelving on both side walls (render-only display casework;
    // the airtight sheets already carry collision at the walls).
    // (A multi-floor grand emporium keeps its -X wall for the stairs.)
    for (const sx of floors > 1 ? [1] : [-1, 1]) {
      const shelfX = sx * (hw - 0.55);
      for (let level = 0; level < 3; level++) {
        boxes.push({ x: shelfX, y: 0.55 + level * 0.75, z: 0.4, w: 0.7, h: 0.08, d: 2 * hd - 4.6, mat: 'trim', collide: false });
      }
      boxes.push({ x: shelfX, y: 1.25, z: 0.4, w: 0.5, h: 2.5, d: 2 * hd - 4.6, mat: 'wall', collide: false });
    }
    // Twin pedestal aisles flanking the walkway from the door.
    const aisleX = Math.min(hw * 0.42, hw - 2.0);
    if (aisleX > 1.8) {
      for (let z = -hd + 3.6; z <= hd - 4.4; z += 2.6) {
        for (const sx of [-1, 1]) {
          boxes.push({ x: sx * aisleX, y: 0.5, z, w: 0.85, h: 1.0, d: 0.85, mat: 'stair', collide: true });
          boxes.push({ x: sx * aisleX, y: 1.06, z, w: 1.0, h: 0.04, d: 1.0, mat: 'trim', collide: false });
        }
      }
    }
    // The service counter: an L before the back wall, gap on the +X side.
    boxes.push({ x: -hw * 0.18, y: 0.55, z: hd - 2.1, w: hw * 1.05, h: 1.1, d: 0.7, mat: 'floor', collide: true });
    boxes.push({ x: -hw + 0.9, y: 0.55, z: hd - 3.2, w: 0.7, h: 1.1, d: 1.6, mat: 'floor', collide: true });
    // A soft runner from the door to the counter.
    boxes.push({ x: 0, y: 0.1, z: 0, w: 2.4, h: 0.03, d: 2 * hd - 5.2, mat: 'stair', collide: false });
  }

  return { floors, hw, hd, boxes, panes, door: { x: 0, z: -hd }, stair, lift };
}

// ─── The enterable-building registry ─────────────────────────────────────────
// Owning modules (settlement builder, deed renderer) register eligible
// buildings with hide/restore closures over their own render + collider
// handles; BuildingInteriorModule consumes it. Pure data — no three imports.

export interface EnterableBuilding {
  /** Five namespaces (THE INSTANCE-SPLIT LAW): 'settle:<site>:…' (CommonsSettlementsModule),
   *  'deed:<structureId>' (StructureRenderModule), 'hivelot:<lotKey>' (MerchantHiveModule),
   *  'preset:<kind>' (PersonalPresetModule), 'mark:<part|placement>:<refId>' (InteriorMarkerSyncModule). */
  id: string;
  name: string;
  key: string;
  x: number; z: number; yaw: number; groundY: number;
  w: number; h: number; d: number;
  /** Remove the exterior render + its collider (the player steps inside). */
  hide(): void;
  /** Put the exterior back (the player left). */
  restore(): void;
}

// globalThis-backed (the instance-split law, terrain_stamp precedent): the
// vite dev graph was observed resolving identical specifiers to distinct
// module instances — a parallel instance with an empty registry silently
// breaks consumers. One shared store is instance-proof.
const GI = globalThis as unknown as { __atlasEnterables?: Map<string, EnterableBuilding> };
export const enterables: Map<string, EnterableBuilding> = (GI.__atlasEnterables ??= new Map());

/** The enterable building whose walkable core contains (x, z), or null.
 *  Used by the versona life layer: a resting versona inside a home is
 *  "indoors" — hidden from the street, present when the player enters. */
export function enterableAt(x: number, z: number): EnterableBuilding | null {
  for (const b of enterables.values()) {
    if (Math.abs(b.x - x) > 40 || Math.abs(b.z - z) > 40) continue; // cheap reject
    const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
    const dx = x - b.x, dz = z - b.z;
    const lx = c * dx - s * dz, lz = s * dx + c * dz;
    if (Math.abs(lx) <= Math.min(b.w, 60) / 2 && Math.abs(lz) <= Math.min(b.d, 60) / 2) return b;
  }
  return null;
}

/**
 * Clamp a world-space point into the interior's LEGAL walkable region: the
 * core rect plus the door corridor on the local -Z face. Indoors the server's
 * position is advisory (it has no walls) — a reconcile correction that targets
 * the 1.6 m airtight wall band embeds the capsule in a box collider it can
 * never depenetrate from, and both unstick watchdogs stand down indoors by
 * law (the stuck-IN-the-wall field bug, 2026-08-15). Margin 0.5 keeps the
 * capsule (r 0.3) clear of the sheets' inner faces, which protrude 0.125 m
 * past the wall plane (IN) — 0.35 would leave the target 7.5 cm embedded.
 */
export function clampIndoor(b: EnterableBuilding, x: number, z: number): { x: number; z: number; clamped: boolean } {
  const hw = Math.min(b.w, 60) / 2 - WALL_IN;
  const hd = Math.min(b.d, 60) / 2 - WALL_IN;
  const m = 0.5;
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  const dx = x - b.x, dz = z - b.z;
  let lx = c * dx - s * dz, lz = s * dx + c * dz;
  const g = DOOR_W / 2 + 0.1;
  if (lz < -hd + m && Math.abs(lx) <= g + 0.6) {
    // Door corridor: hold the corridor width, leave the depth free — past
    // hd + 1.9 the walkedOut threshold exits the interior and the clamp
    // stops applying entirely, so walking out the door is never fought.
    lx = Math.max(-(g - m), Math.min(g - m, lx));
  } else {
    lx = Math.max(-(hw - m), Math.min(hw - m, lx));
    lz = Math.max(-(hd - m), Math.min(hd - m, lz));
  }
  const wx = b.x + lx * c + lz * s;
  const wz = b.z - lx * s + lz * c;
  return { x: wx, z: wz, clamped: Math.abs(wx - x) + Math.abs(wz - z) > 1e-6 };
}

/** World-space door point of an enterable (local door rotated by yaw). */
export function doorWorld(b: EnterableBuilding): { x: number; z: number } {
  const spec = interiorSpec(b.w, b.h, b.d);
  const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
  return {
    x: b.x + spec.door.x * c + spec.door.z * s,
    z: b.z - spec.door.x * s + spec.door.z * c,
  };
}
